// WHEN THE RACING CARD IS BOUGHT WITHOUT ANYBODY ASKING FOR IT.
//
// The owner's complaint: "the racing prices don't refresh unless you go into
// the Racing tab and refresh". Everything else on the pad follows the board —
// it lands at boot, again when the app comes back to the foreground, and again
// on a deliberate Refresh — and the card did not: it was bought on a tab enter
// and on a pull, and on nothing else. So a racing ticket printed at 9:30 in the
// morning was still quoting 9:30's prices at four in the afternoon unless
// somebody went looking for it.
//
// The card follows the same cadence now, INSIDE A BUDGET, and this file is that
// budget as a rule rather than as a habit:
//
//   the key       no PuntersEdge key, no card, and nothing to spend.
//   the codes     no code switched on is a card with nothing on it.
//   the hours     06:00 to 23:30 in Sydney. The first meetings are barriered
//                 around 11 and the last of the night trots are done by 23:00,
//                 so a phone picked up at 3 am buys a card of races that have
//                 all been run.
//   the staleness RACING_STALE_MS, an hour. Twenty minutes is the number a tab
//                 enter uses, because somebody looking at the card is looking at
//                 the price; an hour is the number for a fetch nobody asked for.
//   the cap       RACING_AUTO_PULLS a day, counted per Sydney day and kept on
//                 the device. Six pulls at 2 credits is 12 a day and 360 of the
//                 free 1,500 a month, which leaves the Refresh button, the
//                 movers and the settle their share of the same key.
//
// NOTHING HERE IS ON A TIMER, and nothing here fetches. It answers one question
// — may the app buy a card right now — and `store.js` is the only thing that
// asks it. That division is the same one `codes.js` keeps: the rule is pure and
// testable without a device, the record and the spending live in the store.
//
// PURE, AND A LEAF. `localDate` (tickets.js) and the Sydney zone are the whole
// of its dependencies.

import { RACING_TZ } from './board.js';
import { localDate } from '../tickets.js';

/** How old a card may be before the app buys one on its own: an hour. */
export const RACING_STALE_MS = 60 * 60 * 1000;

/** How many of those the day may have. Six × 2 credits = 12 a day. */
export const RACING_AUTO_PULLS = 6;

/** The first and last minute of the Sydney day an automatic pull may land in. */
export const RACING_AUTO_FROM_MIN = 6 * 60;
export const RACING_AUTO_UNTIL_MIN = 23 * 60 + 30;

/** Where the day's tally lives: `{ date, pulls }`, one row, replaced each day. */
export const RACING_AUTO_PREF = 'pref.racingAutoPulls';

// One formatter per zone, cached: building one is the expensive part and this
// is asked on every resume. The same idiom `results-due.js` uses, and for the
// same reason — `localDate` answers which day it is over there and nothing
// about what the clock reads.
const FORMATTERS = new Map();

function formatterFor(timeZone) {
  let fmt = FORMATTERS.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit',
    });
    FORMATTERS.set(timeZone, fmt);
  }
  return fmt;
}

/**
 * What the wall clock in `timeZone` reads at `at`, as minutes since midnight.
 * `NaN` for an instant that cannot be read, which makes every window test
 * below false: a broken clock buys nothing.
 */
export function minutesOfDay(at, { timeZone = RACING_TZ } = {}) {
  const ms = at instanceof Date ? at.getTime() : Number(at);
  if (!Number.isFinite(ms)) return NaN;
  let hour = NaN;
  let minute = NaN;
  for (const { type, value } of formatterFor(timeZone).formatToParts(ms)) {
    if (type === 'hour') hour = Number(value);
    if (type === 'minute') minute = Number(value);
  }
  return Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : NaN;
}

/** Is this an hour of the Sydney day worth buying a card in? */
export function withinRacingHours(at, { timeZone = RACING_TZ } = {}) {
  const minutes = minutesOfDay(at, { timeZone });
  return minutes >= RACING_AUTO_FROM_MIN && minutes <= RACING_AUTO_UNTIL_MIN;
}

/**
 * Is the card on the device old enough to be worth buying again on its own?
 *
 * No card at all, a card for another day, an unreadable stamp and a card older
 * than `staleMs` are all the same answer. A card for a day that is not today's
 * in Sydney is not stale so much as finished — the races on it have been run —
 * and either way the next one has to be bought.
 */
export function racingCardStale(card, { at = Date.now(), date, staleMs = RACING_STALE_MS } = {}) {
  if (!card?.races?.length) return true;
  if (date && card.forDate !== date) return true;
  const fetchedAt = Date.parse(card.fetchedAt ?? '');
  return !Number.isFinite(fetchedAt) || at - fetchedAt >= staleMs;
}

/** The day's tally as a plain `{ date, pulls }`, whatever is in storage. */
function readTally(stored) {
  const date = typeof stored?.date === 'string' ? stored.date : '';
  const pulls = Number(stored?.pulls);
  return { date, pulls: Number.isFinite(pulls) && pulls > 0 ? Math.floor(pulls) : 0 };
}

/** How many automatic pulls this device has made ON `date`. Zero for any other day. */
export function autoPullsOn(stored, date) {
  const tally = readTally(stored);
  return tally.date === date ? tally.pulls : 0;
}

/** The tally after one more pull on `date`. A new day starts at one. */
export function nextAutoPulls(stored, date) {
  return { date, pulls: autoPullsOn(stored, date) + 1 };
}

/**
 * MAY THE APP BUY A CARD RIGHT NOW, nobody having asked it to?
 *
 * Every clause is a refusal, and the order is cheapest first. `codes` is the
 * list of codes actually switched on (`enabledCodes`), not the stored record:
 * an empty list is a real state to answer, and a record normalises away from it.
 *
 * The demo, the sample board and a break are NOT here. They are refusals about
 * whether this device may spend anything at all — the store makes them, in the
 * one place it makes them for the sports board too — and repeating them would
 * put the break rule in two files that could disagree.
 */
export function shouldAutoPull({
  at = Date.now(),
  card = null,
  keyed = false,
  codes = [],
  stored = null,
  timeZone = RACING_TZ,
} = {}) {
  if (!keyed) return false;
  if (!Array.isArray(codes) || codes.length === 0) return false;
  if (!withinRacingHours(at, { timeZone })) return false;
  const date = localDate(at, timeZone);
  if (autoPullsOn(stored, date) >= RACING_AUTO_PULLS) return false;
  return racingCardStale(card, { at, date });
}

/**
 * "3 pulls today" — what the day has actually cost, for the ⓘ under the
 * PuntersEdge card in Settings.
 *
 * A budget printed as a rule is a promise; this is the receipt. Empty on a day
 * nothing has been bought automatically, because a zero is a line of small
 * print saying nothing happened.
 */
export function autoPullLine(pulls) {
  const n = Number(pulls);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${n} pull${n === 1 ? '' : 's'} today`;
}
